import type { Message, QuickAction, Agent } from '../types';
import { createElement, parseMarkdown, formatTime, scrollToBottom, isScrolledToBottom, generateId, isValidUrl } from '../utils/dom';

/**
 * Message list component
 */
export class MessageList {
  private element: HTMLElement | null = null;
  private messagesContainer: HTMLElement | null = null;
  private messages: Map<string, HTMLElement> = new Map();
  private messageData: Map<string, Message> = new Map();
  private onQuickAction: ((action: QuickAction) => void) | null = null;
  private autoScroll = true;
  private closedMessageElement: HTMLElement | null = null;
  private currentAgent: Agent | null = null;
  private brandLogoUrl: string | undefined;
  private firstAdminMessageId: string | null = null;
  private lastMessageBody: string | undefined;
  private lastMessageAt: string | undefined;
  private onScrollTopCallback: (() => boolean | void | Promise<boolean | void>) | null = null;
  private isLoadingTop = false;
  private suppressScrollEvents = false;

  /**
   * Set quick action handler
   */
  setQuickActionHandler(handler: (action: QuickAction) => void): void {
    this.onQuickAction = handler;
  }

  /**
   * Set callback for when user scrolls to the top (for loading older messages)
   */
  setScrollTopHandler(handler: (() => boolean | void | Promise<boolean | void>) | null): void {
    this.onScrollTopCallback = handler;
  }

  /**
   * Signal that top-loading is complete, re-enabling scroll-top detection.
   * Delays re-enabling by one frame so the scroll restoration from
   * prependMessages() settles before we accept new scroll-to-top triggers.
   */
  finishLoadingTop(): void {
    // Keep isLoadingTop=true for one more frame to let scroll position settle
    requestAnimationFrame(() => {
      this.isLoadingTop = false;
    });
  }

  /**
   * Reset top-pagination state (keeps the scroll-top handler attached)
   */
  resetTopLoading(): void {
    this.isLoadingTop = false;
    this.suppressScrollEvents = false;
  }

  /**
   * Set agent info for message avatars
   */
  setAgent(agent: Agent): void {
    this.currentAgent = agent;
  }

  /**
   * Set the brand logo shown as the avatar of the conversation's first message only
   */
  setBrandLogoUrl(url?: string): void {
    this.brandLogoUrl = url;
    this.refreshFirstAdminMessageAvatar();
  }

  /**
   * Re-render the message currently holding the first-admin-message slot, e.g.
   * after the brand logo changes mid-session. No-ops if none is tracked yet.
   */
  private refreshFirstAdminMessageAvatar(): void {
    if (!this.firstAdminMessageId) return;
    const message = this.messageData.get(this.firstAdminMessageId);
    const el = this.messages.get(this.firstAdminMessageId);
    if (!message || !el) return;
    const rebuiltEl = this.createMessageElement(message, true);
    el.replaceWith(rebuiltEl);
    this.messages.set(this.firstAdminMessageId, rebuiltEl);
  }

  /**
   * Render the message list
   */
  render(): HTMLElement {
    if (this.element) {
      return this.element;
    }

    this.element = createElement('div', {
      className: 'cevro-messages',
      id: generateId(),
    });

    this.messagesContainer = createElement('div', {
      className: 'cevro-messages__container',
      'data-testid': 'messages-container',
    });

    // Track scroll position for auto-scroll and scroll-to-top detection
    this.messagesContainer.addEventListener('scroll', () => {
      if (this.suppressScrollEvents) return;
      this.autoScroll = isScrolledToBottom(this.messagesContainer!);
      if (
        this.messagesContainer!.scrollTop < 80 &&
        this.onScrollTopCallback &&
        !this.isLoadingTop
      ) {
        const result = this.onScrollTopCallback();
        if (result instanceof Promise) {
          this.isLoadingTop = true;
          result
            .then((started) => {
              if (started === false) {
                this.isLoadingTop = false;
              }
            })
            .catch(() => {
              this.isLoadingTop = false;
            });
        } else if (result !== false) {
          this.isLoadingTop = true;
        }
      }
    });

    this.element.appendChild(this.messagesContainer);

    return this.element;
  }

  /**
   * Get the body of the last message added
   */
  getLastMessageBody(): string | undefined {
    return this.lastMessageBody;
  }

  getLastMessageAt(): string | undefined {
    return this.lastMessageAt;
  }

  /**
   * Count consecutive agent messages at the tail, back to the last user message.
   * If the user has replied since, returns 0.
   */
  getAgentMessagesSinceLastUserMessage(): number {
    if (!this.messagesContainer) return 0;
    const nodes = this.messagesContainer.querySelectorAll('.cevro-message');
    let count = 0;
    for (let i = nodes.length - 1; i >= 0; i--) {
      const el = nodes[i];
      if (el.classList.contains('cevro-message--outbound')) {
        // outbound in SDK = user's own message → stop
        break;
      }
      if (el.classList.contains('cevro-message--inbound')) {
        // inbound in SDK = agent's message → count
        count++;
      }
    }
    return count;
  }

  /**
   * Add a message to the list
   */
  addMessage(message: Message, animate = true, skipScroll = false): void {
    if (!this.messagesContainer) return;

    // Track last message for conversation list preview
    if (message.body) {
      this.lastMessageBody = message.body;
      this.lastMessageAt = message.createdAt;
    }

    // Check if message already exists
    if (this.messages.has(message.id)) {
      this.updateMessage(message);
      return;
    }

    this.messageData.set(message.id, message);
    const isFirst = message.inbound !== true && this.firstAdminMessageId === null;
    if (isFirst) {
      this.firstAdminMessageId = message.id;
    }
    const messageEl = this.createMessageElement(message, isFirst);
    if (animate) {
      messageEl.classList.add('cevro-message--animate');
    }

    // Insert before typing indicator if it exists, otherwise append
    const typingIndicator = this.messagesContainer.querySelector('#cevro-typing-indicator');
    if (typingIndicator) {
      this.messagesContainer.insertBefore(messageEl, typingIndicator);
    } else {
      this.messagesContainer.appendChild(messageEl);
    }
    this.messages.set(message.id, messageEl);

    // Auto-scroll if at bottom (skip during bulk load)
    if (this.autoScroll && !skipScroll) {
      scrollToBottom(this.messagesContainer);
    }
  }

  /**
   * Prepend multiple messages at the top (for loading older messages).
   * Performs a single scroll restoration after all inserts to avoid jitter.
   */
  prependMessages(messages: Message[]): void {
    if (!this.messagesContainer || messages.length === 0) return;

    // Anchor: remember the current first child's visual position
    let anchor = this.messagesContainer.firstElementChild as HTMLElement | null;
    const anchorOffset = anchor
      ? anchor.offsetTop - this.messagesContainer.scrollTop
      : 0;

    // These are older than everything currently loaded — if one of them is an
    // admin message, it's the true first-of-conversation, superseding whatever
    // held that slot before (which needs re-rendering without the brand logo).
    const earliestAdminMessage = messages.find(m => m.inbound !== true);
    if (earliestAdminMessage && earliestAdminMessage.id !== this.firstAdminMessageId) {
      const previousFirstId = this.firstAdminMessageId;
      this.firstAdminMessageId = earliestAdminMessage.id;
      const previousFirstMessage = previousFirstId ? this.messageData.get(previousFirstId) : undefined;
      const previousFirstEl = previousFirstId ? this.messages.get(previousFirstId) : undefined;
      if (previousFirstId && previousFirstMessage && previousFirstEl) {
        const rebuiltEl = this.createMessageElement(previousFirstMessage, false);
        previousFirstEl.replaceWith(rebuiltEl);
        this.messages.set(previousFirstId, rebuiltEl);
        // The rebuilt node replaced the DOM element insertBefore() below anchors
        // on — keep the reference valid so the batch insert doesn't throw.
        if (anchor === previousFirstEl) {
          anchor = rebuiltEl;
        }
      }
    }

    // Insert all messages before the anchor in order (oldest → newest)
    for (const message of messages) {
      if (this.messages.has(message.id)) continue;
      this.messageData.set(message.id, message);
      const isFirst = message.id === this.firstAdminMessageId;
      const messageEl = this.createMessageElement(message, isFirst);
      this.messagesContainer.insertBefore(messageEl, anchor);
      this.messages.set(message.id, messageEl);
    }

    // Single scroll restoration: keep the anchor element at the same visual position
    // Suppress scroll events to avoid re-triggering the scroll-to-top handler
    if (anchor) {
      this.suppressScrollEvents = true;
      this.messagesContainer.scrollTop = anchor.offsetTop - anchorOffset;
      // Re-enable after the browser processes the scroll
      requestAnimationFrame(() => { this.suppressScrollEvents = false; });
    }
  }

  /**
   * Prepend a single message at the top while preserving scroll position.
   */
  prependMessage(message: Message): void {
    this.prependMessages([message]);
  }

  /**
   * Update an existing message
   * @param message - Updated message data
   * @param oldId - Original message id (for optimistic updates where id changes)
   */
  updateMessage(message: Message, oldId?: string): void {
    const lookupId = oldId || message.id;
    const existingEl = this.messages.get(lookupId);
    if (!existingEl || !this.messagesContainer) return;

    if (this.firstAdminMessageId === lookupId) {
      this.firstAdminMessageId = message.id;
    }
    const isFirst = this.firstAdminMessageId === message.id;
    this.messageData.set(message.id, message);
    const newEl = this.createMessageElement(message, isFirst);
    this.messagesContainer.replaceChild(newEl, existingEl);

    // Remove old id entry and add new one
    if (oldId && oldId !== message.id) {
      this.messages.delete(oldId);
      this.messageData.delete(oldId);
    }
    this.messages.set(message.id, newEl);
  }

  /**
   * Update upload progress for a message
   */
  updateMessageProgress(messageId: string, progress: number): void {
    const messageEl = this.messages.get(messageId);
    if (!messageEl) return;

    const progressFill = messageEl.querySelector('.cevro-file-progress__fill') as HTMLElement;
    const progressText = messageEl.querySelector('.cevro-file-progress__text');

    if (progressFill) {
      progressFill.style.width = `${progress}%`;
    }
    if (progressText) {
      progressText.textContent = `${progress}%`;
    }
  }

  /**
   * Remove a message from the list
   */
  removeMessage(messageId: string): void {
    const messageEl = this.messages.get(messageId);
    if (!messageEl) return;

    messageEl.remove();
    this.messages.delete(messageId);
    this.messageData.delete(messageId);
    if (this.firstAdminMessageId === messageId) {
      this.firstAdminMessageId = null;
      this.promoteNextAdminMessageToFirst();
    }
  }

  /**
   * Give the first-message treatment to the earliest remaining admin message
   * (by DOM position), e.g. after the previous holder is removed.
   */
  private promoteNextAdminMessageToFirst(): void {
    if (!this.messagesContainer) return;
    for (const child of Array.from(this.messagesContainer.children)) {
      const id = (child as HTMLElement).dataset.messageId;
      if (!id) continue;
      const message = this.messageData.get(id);
      if (!message || message.inbound === true) continue;

      this.firstAdminMessageId = id;
      const rebuiltEl = this.createMessageElement(message, true);
      child.replaceWith(rebuiltEl);
      this.messages.set(id, rebuiltEl);
      return;
    }
  }

  /**
   * Create message element
   */
  private createMessageElement(message: Message, isFirst = false): HTMLElement {
    // From SDK perspective:
    // - inbound: true = user's message (to backend) = show on RIGHT
    // - outbound: true = admin's message (from backend) = show on LEFT
    const isMyMessage = message.inbound === true;
    const wrapper = createElement('div', {
      className: `cevro-message ${isMyMessage ? 'cevro-message--outbound' : 'cevro-message--inbound'}`,
      'data-message-id': message.id,
      'data-testid': `message-${message.id}`,
    });

    // Shared by the avatar block below and the sender-name label further down,
    // so both agree on whether this message is rendered as a human agent.
    const showBrandLogo = !isMyMessage && isFirst && !!this.brandLogoUrl && isValidUrl(this.brandLogoUrl);
    const isHuman = !isMyMessage && !showBrandLogo && message.senderType === 'human';

    // Avatar for admin messages
    if (!isMyMessage) {
      const avatar = createElement('div', {
        className: `cevro-message__avatar${isHuman ? ' cevro-message__avatar--human' : ''}`,
      });

      if (isHuman) {
        // Human agent: show initials avatar
        const name = message.agentName || 'Support';
        const initials = name
          .split(' ')
          .map(w => w[0])
          .join('')
          .toUpperCase()
          .slice(0, 2);
        const initialsEl = createElement('div', {
          className: 'cevro-message__avatar-initials',
        });
        initialsEl.textContent = initials;
        avatar.appendChild(initialsEl);
      } else {
        const validAgentAvatarUrl =
          this.currentAgent?.avatarUrl && isValidUrl(this.currentAgent.avatarUrl)
            ? this.currentAgent.avatarUrl
            : null;
        const avatarSrc = showBrandLogo ? this.brandLogoUrl : validAgentAvatarUrl;
        if (avatarSrc) {
          const img = createElement('img', {
            className: 'cevro-message__avatar-img',
            src: avatarSrc,
            alt: showBrandLogo ? 'Logo' : this.currentAgent?.name || 'Agent',
          }) as HTMLImageElement;
          avatar.appendChild(img);
        } else {
          avatar.innerHTML = this.createBotAvatarSvg();
        }
      }

      wrapper.appendChild(avatar);
    }

    // Message content wrapper
    const contentWrapper = createElement('div', {
      className: 'cevro-message__content-wrapper',
    });

    // Show sender name for human agent messages
    if (isHuman && message.agentName) {
      const senderLabel = createElement('div', {
        className: 'cevro-message__sender-name',
      });
      senderLabel.textContent = message.agentName;
      contentWrapper.appendChild(senderLabel);
    }

    // Message bubble
    const bubble = createElement('div', {
      className: 'cevro-message__bubble',
    });

    // Handle different message types
    const isImage = message.type === 'image' || message.attachment?.category === 'image';

    if (isImage && message.mediaUrl && isValidUrl(message.mediaUrl)) {
      // Image message - show actual image
      const imageContainer = createElement('div', {
        className: 'cevro-message__image-container',
      });

      const img = createElement('img', {
        className: 'cevro-message__image',
      }) as HTMLImageElement;
      img.src = message.mediaUrl;
      img.alt = message.attachment?.filename || 'Image';
      img.loading = 'lazy';
      imageContainer.appendChild(img);

      // Show progress overlay if uploading
      if (message.status === 'sending') {
        const progressOverlay = this.createProgressOverlay();
        imageContainer.appendChild(progressOverlay);
      }

      bubble.appendChild(imageContainer);
    } else if (message.type === 'file' || (message.attachment && !isImage)) {
      // File attachment (non-image) - show file card
      const fileCard = this.createFileCard(message);
      bubble.appendChild(fileCard);
    } else if (message.type === 'media' && message.mediaUrl && isValidUrl(message.mediaUrl)) {
      // Legacy media type
      const link = createElement('a', {
        className: 'cevro-message__attachment',
      }) as HTMLAnchorElement;
      link.href = message.mediaUrl;
      link.target = '_blank';
      link.rel = 'noopener';
      link.innerHTML = `
        ${this.getFileIcon('file')}
        <span>Attachment</span>
      `;
      bubble.appendChild(link);
    }

    // Text content
    if (message.body) {
      const textContent = createElement('div', {
        className: 'cevro-message__text',
      });
      textContent.innerHTML = parseMarkdown(message.body);
      bubble.appendChild(textContent);
    }

    contentWrapper.appendChild(bubble);

    // Quick actions
    if (message.richContent?.quickActions?.length) {
      const actionsContainer = createElement('div', {
        className: 'cevro-message__quick-actions',
      });

      message.richContent.quickActions.forEach((action) => {
        const button = createElement('button', {
          className: 'cevro-message__quick-action',
        });
        button.textContent = action.label;
        button.addEventListener('click', () => {
          if (this.onQuickAction) {
            this.onQuickAction(action);
          }
        });
        actionsContainer.appendChild(button);
      });

      contentWrapper.appendChild(actionsContainer);
    }

    // Timestamp and status
    const meta = createElement('div', {
      className: 'cevro-message__meta',
    });
    meta.textContent = formatTime(message.createdAt);

    // Status indicator for user's own messages
    if (isMyMessage && message.status) {
      const status = createElement('span', {
        className: `cevro-message__status cevro-message__status--${message.status}`,
      });
      status.innerHTML = this.getStatusIcon(message.status);
      meta.appendChild(status);
    }

    contentWrapper.appendChild(meta);
    wrapper.appendChild(contentWrapper);

    return wrapper;
  }

  /**
   * Create progress overlay for images
   */
  private createProgressOverlay(): HTMLElement {
    const overlay = createElement('div', {
      className: 'cevro-file-progress-overlay',
    });
    overlay.innerHTML = `
      <div class="cevro-file-progress">
        <div class="cevro-file-progress__bar">
          <div class="cevro-file-progress__fill" style="width: 0%"></div>
        </div>
        <span class="cevro-file-progress__text">0%</span>
      </div>
    `;
    return overlay;
  }

  /**
   * Create file card for non-image attachments
   */
  private createFileCard(message: Message): HTMLElement {
    const attachment = message.attachment;
    const category = attachment?.category || 'file';
    const filename = attachment?.filename || 'File';
    const size = attachment?.size || 0;
    const isUploading = message.status === 'sending';

    const card = createElement('div', {
      className: `cevro-file-card cevro-file-card--${category}`,
    });

    // File icon
    const iconWrapper = createElement('div', {
      className: 'cevro-file-card__icon',
    });
    iconWrapper.innerHTML = this.getFileIcon(category);
    card.appendChild(iconWrapper);

    // File info
    const info = createElement('div', {
      className: 'cevro-file-card__info',
    });

    const name = createElement('div', {
      className: 'cevro-file-card__name',
    });
    name.textContent = this.truncateFilename(filename, 25);
    name.title = filename;
    info.appendChild(name);

    const details = createElement('div', {
      className: 'cevro-file-card__details',
    });
    details.textContent = this.formatFileSize(size);
    info.appendChild(details);

    card.appendChild(info);

    // Progress or download
    if (isUploading) {
      const progress = createElement('div', {
        className: 'cevro-file-card__progress',
      });
      progress.innerHTML = `
        <div class="cevro-file-progress">
          <div class="cevro-file-progress__bar">
            <div class="cevro-file-progress__fill" style="width: 0%"></div>
          </div>
          <span class="cevro-file-progress__text">0%</span>
        </div>
      `;
      card.appendChild(progress);
    } else if (message.mediaUrl && isValidUrl(message.mediaUrl)) {
      // Make card clickable to download
      card.style.cursor = 'pointer';
      card.addEventListener('click', () => {
        window.open(message.mediaUrl, '_blank');
      });
    }

    return card;
  }

  /**
   * Get file icon SVG based on category
   */
  private getFileIcon(category: string): string {
    const icons: Record<string, string> = {
      pdf: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" fill="#E53935"/>
        <path d="M14 2V8H20" fill="#FFCDD2"/>
        <path d="M9 13H10.5V14.5H9V16H7.5V11H10.5V12.5H9V13Z" fill="white"/>
        <path d="M11.5 11H13.5C14.05 11 14.5 11.45 14.5 12V14C14.5 14.55 14.05 15 13.5 15H12.5V16H11.5V11ZM13 14V12H12.5V14H13Z" fill="white"/>
        <path d="M15.5 11H17V12H16V13H17V14H16V16H15.5V11Z" fill="white"/>
      </svg>`,
      document: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" fill="#1976D2"/>
        <path d="M14 2V8H20" fill="#BBDEFB"/>
        <path d="M8 12H16V13H8V12ZM8 15H14V16H8V15Z" fill="white"/>
      </svg>`,
      spreadsheet: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" fill="#388E3C"/>
        <path d="M14 2V8H20" fill="#C8E6C9"/>
        <path d="M8 12H10V14H8V12ZM11 12H13V14H11V12ZM14 12H16V14H14V12ZM8 15H10V17H8V15ZM11 15H13V17H11V15Z" fill="white"/>
      </svg>`,
      presentation: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" fill="#F57C00"/>
        <path d="M14 2V8H20" fill="#FFE0B2"/>
        <rect x="8" y="12" width="8" height="5" rx="1" fill="white"/>
      </svg>`,
      video: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" fill="#7B1FA2"/>
        <path d="M14 2V8H20" fill="#E1BEE7"/>
        <path d="M10 11V17L15 14L10 11Z" fill="white"/>
      </svg>`,
      audio: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" fill="#00897B"/>
        <path d="M14 2V8H20" fill="#B2DFDB"/>
        <path d="M12 11C10.9 11 10 11.9 10 13V15C10 16.1 10.9 17 12 17C13.1 17 14 16.1 14 15V13C14 11.9 13.1 11 12 11Z" fill="white"/>
        <path d="M15 14V15C15 16.66 13.66 18 12 18C10.34 18 9 16.66 9 15V14H8V15C8 17.03 9.53 18.71 11.5 18.96V20H10V21H14V20H12.5V18.96C14.47 18.71 16 17.03 16 15V14H15Z" fill="white"/>
      </svg>`,
      archive: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" fill="#795548"/>
        <path d="M14 2V8H20" fill="#D7CCC8"/>
        <rect x="10" y="11" width="4" height="2" fill="white"/>
        <rect x="10" y="14" width="4" height="2" fill="white"/>
        <rect x="10" y="17" width="4" height="2" fill="white"/>
      </svg>`,
      text: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" fill="#607D8B"/>
        <path d="M14 2V8H20" fill="#CFD8DC"/>
        <path d="M8 12H16V13H8V12ZM8 15H14V16H8V15ZM8 18H16V19H8V18Z" fill="white"/>
      </svg>`,
      image: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" fill="#43A047"/>
        <path d="M14 2V8H20" fill="#C8E6C9"/>
        <circle cx="10" cy="13" r="2" fill="white"/>
        <path d="M8 18L10 15L12 17L15 13L17 18H8Z" fill="white"/>
      </svg>`,
      file: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" fill="#90A4AE"/>
        <path d="M14 2V8H20" fill="#CFD8DC"/>
      </svg>`,
    };

    return icons[category] || icons.file;
  }

  /**
   * Format file size to human readable
   */
  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  /**
   * Truncate filename if too long
   */
  private truncateFilename(filename: string, maxLength: number): string {
    if (filename.length <= maxLength) return filename;

    const ext = filename.split('.').pop() || '';
    const name = filename.slice(0, filename.length - ext.length - 1);
    const truncatedName = name.slice(0, maxLength - ext.length - 4) + '...';
    return truncatedName + '.' + ext;
  }

  /**
   * Create bot avatar SVG
   */
  private createBotAvatarSvg(): string {
    return `
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="14" cy="10" r="4" fill="white"/>
        <path d="M7 22c0-3.866 3.134-7 7-7s7 3.134 7 7" stroke="white" stroke-width="2" stroke-linecap="round"/>
      </svg>
    `;
  }

  /**
   * Get status icon
   * - sending: single checkmark (message is being sent)
   * - sent: double checkmark (message delivered to server)
   * - delivered: double checkmark (same as sent)
   * - read: double checkmark filled (message was read)
   * - failed: X icon in red
   */
  private getStatusIcon(status: string): string {
    // Single checkmark
    const singleCheck = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>';
    // Double checkmark
    const doubleCheck = '<svg width="16" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="18 7 9.5 17 5 12"/><polyline points="23 7 14.5 17 12 14"/></svg>';
    // Double checkmark filled (for read)
    const doubleCheckRead = '<svg width="16" height="12" viewBox="0 0 24 24" fill="none" stroke="#8B5CF6" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="18 7 9.5 17 5 12"/><polyline points="23 7 14.5 17 12 14"/></svg>';

    switch (status) {
      case 'sending':
        return singleCheck;
      case 'sent':
      case 'delivered':
        return doubleCheck;
      case 'read':
        return doubleCheckRead;
      case 'failed':
        return '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>';
      default:
        return '';
    }
  }

  /**
   * Show typing indicator
   */
  showTypingIndicator(): void {
    if (!this.messagesContainer) return;

    // Remove existing typing indicator
    this.hideTypingIndicator();

    const indicator = createElement('div', {
      className: 'cevro-typing-indicator',
      id: 'cevro-typing-indicator',
      'data-testid': 'typing-indicator',
    });

    indicator.innerHTML = `
      <div class="cevro-typing-indicator__avatar">
        ${this.createBotAvatarSvg()}
      </div>
      <div class="cevro-typing-indicator__dots">
        <span></span>
        <span></span>
        <span></span>
      </div>
    `;

    this.messagesContainer.appendChild(indicator);

    if (this.autoScroll) {
      scrollToBottom(this.messagesContainer);
    }
  }

  /**
   * Hide typing indicator
   */
  hideTypingIndicator(): void {
    // Use querySelector on container (works with Shadow DOM)
    const indicator = this.messagesContainer?.querySelector('#cevro-typing-indicator');
    if (indicator) {
      indicator.remove();
    }
  }

  /**
   * Clear all messages
   */
  clear(): void {
    if (this.messagesContainer) {
      this.messagesContainer.innerHTML = '';
    }
    this.messages.clear();
    this.messageData.clear();
    this.firstAdminMessageId = null;
    this.lastMessageBody = undefined;
    this.lastMessageAt = undefined;
    this.autoScroll = true;
    this.resetTopLoading();
    // Also remove closed message if present
    if (this.closedMessageElement) {
      this.closedMessageElement.remove();
      this.closedMessageElement = null;
    }
  }

  /**
   * Show closed chat message at the bottom of messages area
   * This displays when a ticket is closed (e.g., escalated and resolved)
   * @param message - Custom message text (optional)
   */
  showClosedMessage(message?: string): void {
    if (!this.messagesContainer) return;

    // Remove existing closed message
    this.hideClosedMessage();

    this.closedMessageElement = createElement('div', {
      className: 'cevro-messages__closed-notice',
      'data-testid': 'closed-chat-message',
    });

    const displayMessage = message || 'The agent closed the chat. If you need assistance please start a new chat.';
    this.closedMessageElement.innerHTML = `
      <p class="cevro-messages__closed-text">
        ${displayMessage}
      </p>
    `;

    this.messagesContainer.appendChild(this.closedMessageElement);

    // Scroll to show the closed message
    if (this.autoScroll) {
      scrollToBottom(this.messagesContainer);
    }
  }

  /**
   * Hide closed chat message
   */
  hideClosedMessage(): void {
    if (this.closedMessageElement) {
      this.closedMessageElement.remove();
      this.closedMessageElement = null;
    }
  }

  /**
   * Scroll to bottom
   */
  scrollToBottom(): void {
    if (this.messagesContainer) {
      scrollToBottom(this.messagesContainer, false);
    }
  }

  /**
   * Destroy the component
   */
  destroy(): void {
    if (this.element && this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
    this.element = null;
    this.messagesContainer = null;
    this.messages.clear();
    this.messageData.clear();
    this.firstAdminMessageId = null;
  }
}
